import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { Megaphone, TrendingUp, TrendingDown, Trophy, Download } from "lucide-react";
import { API } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const RANGES = [
  { key: "month", label: "This month" },
  { key: "90", label: "Last 90 days" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
];
const rangeParams = (key) => {
  const today = new Date(); const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "month") return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)) };
  if (key === "90") return { start: iso(new Date(today.getTime() - 90 * 86400000)) };
  if (key === "ytd") return { start: `${today.getFullYear()}-01-01` };
  return {};
};

function RoiBadge({ pct }) {
  if (pct == null) return <span className="text-[10px] text-slate-400 font-mono-plex">no spend</span>;
  const pos = pct >= 0;
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono-plex font-bold ${pos ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{pos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {pct > 0 ? "+" : ""}{pct}%</span>;
}

function RoiTable({ rows, nameKey, idKey, linkTo, testPrefix }) {
  const maxRev = Math.max(...rows.map(r => Math.max(r.revenue, r.spent)), 1);
  if (!rows.length) return <div className="text-sm text-slate-400 p-4">No marketing activity in this range.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">
        <tr><th className="p-2.5">{nameKey === "facility_name" ? "Facility" : "Rep"}</th><th className="p-2.5">Events</th><th className="p-2.5 w-1/3">Spend vs revenue</th><th className="p-2.5 text-right">Net</th><th className="p-2.5 text-right">ROI</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r[idKey] || i} className="border-t border-slate-100" data-testid={`${testPrefix}-${r[idKey]}`}>
            <td className="p-2.5 font-medium text-slate-900">{linkTo ? <Link to={linkTo(r)} className="hover:underline">{r[nameKey] || "—"}</Link> : (r[nameKey] || "—")}<div className="text-[10px] text-slate-500 font-mono-plex">{r.attendees} attendees{r.cost_per_attendee != null ? ` · ${money(r.cost_per_attendee)}/head` : ""}</div></td>
            <td className="p-2.5 font-mono-plex">{r.count}</td>
            <td className="p-2.5">
              <div className="space-y-1">
                <div className="flex items-center gap-2"><div className="h-1.5 rounded-full bg-orange-400" style={{ width: `${(r.spent / maxRev) * 100}%` }} /><span className="text-[10px] font-mono-plex text-slate-500">{money(r.spent)} spent</span></div>
                <div className="flex items-center gap-2"><div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${(r.revenue / maxRev) * 100}%` }} /><span className="text-[10px] font-mono-plex text-slate-500">{money(r.revenue)} revenue</span></div>
              </div>
            </td>
            <td className={`p-2.5 text-right font-mono-plex font-bold ${r.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.net < 0 ? "-" : ""}{money(Math.abs(r.net))}</td>
            <td className="p-2.5 text-right"><RoiBadge pct={r.roi_pct} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function MarketingROI() {
  const [range, setRange] = useState("90");
  const [view, setView] = useState("facility");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/marketing/roi", { params: rangeParams(range) }).then(({ data }) => setData(data)).catch(() => setData(null)); }, [range]);
  if (!data) return null;
  const t = data.total;
  const rows = view === "facility" ? data.by_facility : data.by_rep;
  const exportXlsx = async () => {
    const token = localStorage.getItem("aceh_token") || "";
    const qs = new URLSearchParams(rangeParams(range));
    const r = await fetch(`${API}/marketing/roi/export.xlsx?${qs}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `marketing-roi-${range}.xlsx`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Card className="border-slate-200 shadow-none overflow-hidden" data-testid="marketing-roi">
      <div className="p-5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><Megaphone className="w-3 h-3" /> Marketing ROI</div>
          <h3 className="font-heading font-bold text-xl">Revenue vs spend</h3>
          <p className="text-xs text-slate-500 mt-0.5">Which events and which reps turn lunch-and-learns into referrals.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Tabs value={range} onValueChange={setRange}><TabsList>{RANGES.map(r => <TabsTrigger key={r.key} value={r.key} data-testid={`roi-range-${r.key}`}>{r.label}</TabsTrigger>)}</TabsList></Tabs>
          <Tabs value={view} onValueChange={setView}><TabsList><TabsTrigger value="facility" data-testid="roi-view-facility">By facility</TabsTrigger><TabsTrigger value="rep" data-testid="roi-view-rep">By rep</TabsTrigger></TabsList></Tabs>
          <button onClick={exportXlsx} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-slate-200 bg-white text-xs font-medium hover:bg-slate-50" data-testid="roi-export"><Download className="w-3.5 h-3.5" /> Excel</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 border-b border-slate-100" data-testid="roi-totals">
        {[["Spent", money(t.spent), "text-orange-600"], ["Revenue", money(t.revenue), "text-emerald-700"], ["Net", `${t.net < 0 ? "-" : ""}${money(Math.abs(t.net))}`, t.net >= 0 ? "text-emerald-700" : "text-rose-700"], ["ROI", t.roi_pct == null ? "—" : `${t.roi_pct > 0 ? "+" : ""}${t.roi_pct}%`, t.roi_pct == null || t.roi_pct >= 0 ? "text-slate-900" : "text-rose-700"]].map(([l, v, c]) => (
          <div key={l} className="p-4"><div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">{l}</div><div className={`font-heading font-black text-2xl ${c}`}>{v}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3">
        <div className="xl:col-span-2 overflow-x-auto"><RoiTable rows={rows} nameKey={view === "facility" ? "facility_name" : "rep_name"} idKey={view === "facility" ? "facility_id" : "rep_id"} linkTo={view === "facility" ? (r) => `/facilities/${r.facility_id}` : null} testPrefix={`roi-${view}`} /></div>
        <div className="border-t xl:border-t-0 xl:border-l border-slate-100 p-4" data-testid="roi-top-events">
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1 mb-2"><Trophy className="w-3 h-3 text-amber-500" /> Best events</div>
          {data.top_events.length === 0 && <div className="text-xs text-slate-400">Log marketing activities on a facility to see them ranked here.</div>}
          <div className="space-y-2">
            {data.top_events.map((e, i) => (
              <div key={e.activity_id} className="flex items-start justify-between gap-2 text-sm">
                <div className="min-w-0"><div className="font-medium text-slate-900 truncate">{i + 1}. {e.title}</div><div className="text-[10px] text-slate-500 font-mono-plex truncate">{e.facility_name} · {e.created_by_name} · {e.activity_date}</div></div>
                <div className={`text-xs font-mono-plex font-bold shrink-0 ${e.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{e.net < 0 ? "-" : "+"}{money(Math.abs(e.net))}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
